import React from 'react';
import api from '../../utils/axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { FileText, FileSpreadsheet, FileDown, Search, X } from 'lucide-react';

// Convierte a número aceptando también strings localizados es-AR ("$ 543.503,00").
export const toNumber = (v) => {
  if (typeof v === 'number') return v;
  if (v === null || v === undefined || v === '') return NaN;
  const s = String(v).replace(/[^0-9,.-]/g, '').replace(/\./g, '').replace(',', '.');
  return parseFloat(s);
};

export const fmtMoney = (v) => {
  const n = toNumber(v);
  return Number.isFinite(n)
    ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n)
    : '—';
};

export const fmtK = (n) => {
  const v = toNumber(n) || 0;
  if (Math.abs(v) >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (Math.abs(v) >= 1e3) return `$${Math.round(v / 1e3)}k`;
  return `$${Math.round(v)}`;
};

export const ESTADOS = ['Vigente', 'Mora', 'Cancelado', 'Autorizado', 'Rechazado'];
export const FRECUENCIAS = ['Diaria', 'Semanal', 'Quincenal', 'Mensual'];

const cleanParams = (p) => {
  const out = {};
  Object.entries(p || {}).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') out[k] = v; });
  return out;
};

export async function descargarInforme(tipo, formato, params) {
  try {
    toast.info(`Generando informe ${formato.toUpperCase()}…`);
    const r = await api.get('/motor/informe', { params: { tipo, formato, ...cleanParams(params) }, responseType: 'blob' });
    const url = window.URL.createObjectURL(r.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tipo}_${new Date().toISOString().slice(0, 10)}.${formato}`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    toast.error('No se pudo generar el informe');
  }
}

export const InformeButtons = ({ tipo, params }) => (
  <div className="flex items-center gap-1.5" data-testid="informe-buttons">
    <Button variant="outline" size="sm" onClick={() => descargarInforme(tipo, 'xlsx', params)} data-testid="informe-xlsx" title="Excel">
      <FileSpreadsheet className="h-4 w-4 mr-1 text-emerald-600" /> Excel
    </Button>
    <Button variant="outline" size="sm" onClick={() => descargarInforme(tipo, 'pdf', params)} data-testid="informe-pdf" title="PDF">
      <FileText className="h-4 w-4 mr-1 text-rose-600" /> PDF
    </Button>
    <Button variant="outline" size="sm" onClick={() => descargarInforme(tipo, 'csv', params)} data-testid="informe-csv" title="CSV">
      <FileDown className="h-4 w-4 mr-1 text-slate-500" /> CSV
    </Button>
  </div>
);

const selCls = 'h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm text-slate-700 dark:text-slate-200';

// Barra de filtros reutilizable. `dateFields` = [{value,label}] (vacío => sin selector de tipo de fecha)
export const FiltrosPrestamos = ({ value, onChange, onApply, dateFields = [], showEstado = true, showFrecuencia = true }) => {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const limpiar = () => { const cleared = { date_field: dateFields[0]?.value || '', desde: '', hasta: '', estado: '', frecuencia: '', q: '' }; onChange(cleared); onApply(cleared); };
  return (
    <div className="flex flex-wrap items-end gap-2 mb-4 p-3 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40" data-testid="filtros-prestamos">
      {dateFields.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-[11px] text-slate-500">Filtrar por fecha</label>
          <select className={selCls} value={value.date_field || ''} onChange={(e) => set('date_field', e.target.value)} data-testid="filtros-date-field">
            {dateFields.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
      )}
      <div className="flex flex-col gap-1">
        <label className="text-[11px] text-slate-500">Desde</label>
        <Input type="date" className="h-9 w-[150px]" value={value.desde || ''} onChange={(e) => set('desde', e.target.value)} data-testid="filtros-desde" />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-[11px] text-slate-500">Hasta</label>
        <Input type="date" className="h-9 w-[150px]" value={value.hasta || ''} onChange={(e) => set('hasta', e.target.value)} data-testid="filtros-hasta" />
      </div>
      {showEstado && (
        <div className="flex flex-col gap-1">
          <label className="text-[11px] text-slate-500">Estado</label>
          <select className={selCls} value={value.estado || ''} onChange={(e) => set('estado', e.target.value)} data-testid="filtros-estado">
            <option value="">Todos</option>
            {ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      {showFrecuencia && (
        <div className="flex flex-col gap-1">
          <label className="text-[11px] text-slate-500">Frecuencia</label>
          <select className={selCls} value={value.frecuencia || ''} onChange={(e) => set('frecuencia', e.target.value)} data-testid="filtros-frecuencia">
            <option value="">Todas</option>
            {FRECUENCIAS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      )}
      <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
        <label className="text-[11px] text-slate-500">Buscar</label>
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="h-9 pl-8" placeholder="CUIT o nombre" value={value.q || ''} onChange={(e) => set('q', e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') onApply(value); }} data-testid="filtros-q" />
        </div>
      </div>
      <Button size="sm" className="h-9 bg-teal-600 hover:bg-teal-700 text-white" onClick={() => onApply(value)} data-testid="filtros-aplicar">Aplicar</Button>
      <Button size="sm" variant="ghost" className="h-9" onClick={limpiar} data-testid="filtros-limpiar"><X className="h-4 w-4 mr-1" />Limpiar</Button>
    </div>
  );
};
